
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PublicTestimonials from "@/components/PublicTestimonials";
import { ArrowLeft } from "lucide-react";

const TestimonialPreview = () => {
  const [showAll, setShowAll] = useState(false);
  const [limit, setLimit] = useState(6);
  const navigate = useNavigate();

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Testimonial Preview</h1>
          <p className="text-muted-foreground">
            Preview how testimonials will appear on the public-facing website
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/dashboard/testimonials")}
          className="flex items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back to Management
        </Button>
      </div>

      <Separator />

      <Card>
        <CardHeader>
          <CardTitle>Preview Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show-all">Show All Testimonials</Label>
              <p className="text-sm text-muted-foreground">
                Toggle to show all testimonials, including unapproved ones (admin preview only)
              </p>
            </div>
            <Switch
              id="show-all"
              checked={showAll}
              onCheckedChange={setShowAll}
            />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label htmlFor="limit-slider">Testimonial Limit</Label>
              <span className="text-sm font-medium">{limit}</span>
            </div>
            <Slider
              id="limit-slider"
              min={1}
              max={12}
              step={1}
              value={[limit]}
              onValueChange={(values) => setLimit(values[0])}
            />
            <p className="text-xs text-muted-foreground">
              Control how many testimonials are displayed in each section
            </p>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-8">
          <TabsTrigger value="all">All Testimonials</TabsTrigger>
          <TabsTrigger value="consulting">Consulting</TabsTrigger>
          <TabsTrigger value="training">Training</TabsTrigger>
          <TabsTrigger value="coaching">Coaching</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-0">
          <div className="border rounded-lg bg-slate-50 p-4">
            <PublicTestimonials limit={limit} showAll={showAll} previewMode={true} />
          </div>
        </TabsContent>

        <TabsContent value="consulting" className="mt-0">
          <div className="border rounded-lg bg-slate-50 p-4">
            <PublicTestimonials 
              serviceCategory="Consulting" 
              title="Consulting Services" 
              subtitle="Strategic guidance for your business challenges" 
              limit={limit} 
              showAll={showAll}
              previewMode={true}
            />
          </div>
        </TabsContent>

        <TabsContent value="training" className="mt-0">
          <div className="border rounded-lg bg-slate-50 p-4">
            <PublicTestimonials 
              serviceCategory="Training" 
              title="Training Programs" 
              subtitle="Skill development for teams and individuals" 
              limit={limit} 
              showAll={showAll}
              previewMode={true}
            />
          </div>
        </TabsContent>

        <TabsContent value="coaching" className="mt-0">
          <div className="border rounded-lg bg-slate-50 p-4">
            <PublicTestimonials 
              serviceCategory="Coaching" 
              title="Professional Coaching" 
              subtitle="Personalized guidance for career advancement" 
              limit={limit} 
              showAll={showAll}
              previewMode={true}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TestimonialPreview;
